import type { ButtonConfig } from "@/components/common/Complete";
import { fetchConsentViewsByDiaryId } from "@/features/consent/api/selectConsentById";
import { diariesByPatientQueryOptions } from "@/features/diaries/api/diaryQueryOptions";
import type { FormsAuthCtx } from "@/features/diaries/features/new/hooks/useFormsToDo";
import { getProfileCopy } from "@/features/i18n/profileCopy";
import type { SupportedLocale } from "@patient-watch/i18n";
import type { ProfileT } from "@patient-watch/types";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { ConsentViewT } from "@/types/database/consents";
import type { QueryClient } from "@tanstack/react-query";
import type { RolesT } from "@/utils/roles";

import {
    arePriorDiaryPhasesOutstanding,
    buildConsentTodos,
    buildFormsTodos,
    buildOnboardingTodos,
    buildSetupTodos,
    countOutstandingJourneyTodos,
    isSetupApplicableForDiary,
    type JourneyTodos,
    type OnboardingLabels,
    type TodoItem,
} from "../journeyTodos";
import { onboardingRoutes } from "../onboardingFlow";
import { isOnboardingEmailSkipped, isOnboardingPhoneSkipped } from "./onboardingContactSkip";
import { getNextJourneyPhase } from "./getNextJourneyPhase";

export type PatientResumeDestination = Pick<ButtonConfig, "to" | "params" | "search">;

const OPTIONAL_ONBOARDING_KEYS = new Set(["email", "phone"]);

const ONBOARDING_KEY_TO_ROUTE: Record<string, string> = {
    name: onboardingRoutes.name,
    dateOfBirth: onboardingRoutes.dateOfBirth,
    email: onboardingRoutes.email,
    phone: onboardingRoutes.phone,
    notifications: onboardingRoutes.notifications,
    organisation: onboardingRoutes.name,
};

export function getOnboardingRouteForTodoKey(key: string): string {
    return ONBOARDING_KEY_TO_ROUTE[key] ?? onboardingRoutes.name;
}

export function profileCopyToOnboardingLabels(locale: SupportedLocale): OnboardingLabels {
    const copy = getProfileCopy(locale);
    return {
        name: copy.name,
        dateOfBirth: copy.dateOfBirth,
        email: copy.email,
        phone: copy.phone,
        organisation: copy.organisation,
    };
}

export function buildPatientJourneyTodos(
    profile: ProfileT,
    diary: StatefulDiaryT | null | undefined,
    userId: string,
    consents: ConsentViewT[],
    authCtx: FormsAuthCtx,
    labels: OnboardingLabels
): JourneyTodos {
    const setup = diary ? buildSetupTodos(diary) : [];
    const consent = diary ? buildConsentTodos(consents, userId) : [];
    const priorPhasesOutstanding = arePriorDiaryPhasesOutstanding({ setup, consent });

    return {
        onboarding: buildOnboardingTodos(profile, labels),
        setup,
        consent,
        forms: diary ? buildFormsTodos(diary, authCtx, { priorPhasesOutstanding }) : [],
    };
}

/** True when the patient already progressed past Profile (forms submitted, etc.). */
function hasPostOnboardingDiaryProgress(diary: StatefulDiaryT | null | undefined): boolean {
    if (!diary) return false;
    return (diary.trackers_view ?? []).some((tracker) => (tracker.entries_view?.length ?? 0) > 0);
}

/**
 * Incomplete onboarding items that should hard-redirect into the wizard.
 * Optional email/phone stay incomplete in the UI, but after Skip (or later diary
 * progress) we do not force the patient back into /onboarding/*.
 */
function incompleteOnboardingForRedirect(
    incompleteOnboarding: TodoItem[],
    profile: ProfileT,
    diary: StatefulDiaryT | null | undefined
): TodoItem[] {
    const meta = profile.user_meta_data;
    const pastWizard = hasPostOnboardingDiaryProgress(diary);

    return incompleteOnboarding.filter((item) => {
        if (!OPTIONAL_ONBOARDING_KEYS.has(item.key)) return true;
        if (item.key === "phone" && isOnboardingPhoneSkipped(meta)) return false;
        if (item.key === "email" && isOnboardingEmailSkipped(meta)) return false;
        if (pastWizard) return false;
        return true;
    });
}

/** First incomplete Profile step, or next Consent / Set-up / Forms work for this diary. */
export function getPatientResumeDestination(input: {
    profile: ProfileT;
    roles: RolesT;
    diary: StatefulDiaryT | null | undefined;
    userId: string;
    consents?: ConsentViewT[];
    labels: OnboardingLabels;
}): PatientResumeDestination & { hasOutstandingJourneyWork: boolean } {
    const { profile, roles, diary, userId, labels } = input;
    const authCtx: FormsAuthCtx = { roles: roles as FormsAuthCtx["roles"] };
    const consents = input.consents ?? [];
    const todos = buildPatientJourneyTodos(profile, diary, userId, consents, authCtx, labels);

    const incompleteOnboarding = todos.onboarding.filter((item) => !item.done);
    const forceOnboarding = incompleteOnboardingForRedirect(incompleteOnboarding, profile, diary);
    const firstForced = forceOnboarding[0];
    if (firstForced || roles.hasRole === false) {
        return {
            to: getOnboardingRouteForTodoKey(firstForced?.key ?? "name"),
            hasOutstandingJourneyWork: true,
        };
    }

    if (!diary) {
        return { to: "/diaries", hasOutstandingJourneyWork: false };
    }

    const diaryId = diary.diary_id;
    const outstanding = countOutstandingJourneyTodos(todos);

    if (outstanding === 0) {
        return {
            to: "/diaries/$diaryId/home/to-do",
            params: { diaryId },
            hasOutstandingJourneyWork: false,
        };
    }

    const next = getNextJourneyPhase({
        diary,
        todos,
        diaryId,
        setupApplicable: isSetupApplicableForDiary(diary),
    });

    if (next.phase === "done") {
        return {
            to: "/diaries/$diaryId/home/to-do",
            params: { diaryId },
            hasOutstandingJourneyWork: false,
        };
    }

    return {
        ...next.route,
        hasOutstandingJourneyWork: true,
    };
}

/** Load diary + consent data and resolve where a returning patient should land. */
export async function resolvePatientResumeDestination(
    queryClient: QueryClient,
    userId: string,
    profile: ProfileT,
    roles: RolesT,
    locale: SupportedLocale
): Promise<PatientResumeDestination & { hasOutstandingJourneyWork: boolean }> {
    const labels = profileCopyToOnboardingLabels(locale);
    const diaries = await queryClient.ensureQueryData(diariesByPatientQueryOptions(userId));
    const diary = diaries[0] ?? null;
    const consents = diary ? await fetchConsentViewsByDiaryId(diary.diary_id) : [];

    return getPatientResumeDestination({
        profile,
        roles,
        diary,
        userId,
        consents,
        labels,
    });
}
